import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import {
  setPostAcqUWMapState
} from 'store/actions';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { format } from 'date-fns';
import Scatterplot from 'services/Scatterplot';
import {
  Typography,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ErrorBoundary from 'components/hoc/ErrorBoundary';
import {
  UnderwriteAVM,
  UnderwriteSchoolRating,
  UnderwriteNotes,
  UnderwritePictures,
  UnderwriteWarningBanner
} from 'components/underwrite/shared';
import debounce from 'lodash/debounce';
import { MapWrapper } from 'components/map';
import { sendClientLog } from 'services/logService';
import {
  PostAcqUWGrid,
  PostAcqUWHeader,
  PostAcqUWMap
} from 'components/underwrite/postAcquisition/postAcqUnderwrite';
import { Constants } from 'utilities/constants';
import { PostAcqHistoryTabs } from 'components/underwrite/postAcquisition/postAcqUnderwrite/header';

const PostAcqUWContent = ({ classes, selectedOpportunity,
  zoom, center, gestureHandling, mapViewType,
  setPostAcqUWMapState, selectedTaskHistory, selectedTaskEventHistory,
  viewingReadonlyMode, isUWModal
}) => {
  const [key, setKey] = useState(new Date().toString());

  const onZoomChanged = debounce((map) => {
    if (!map) return;
    setPostAcqUWMapState({ zoom: map.getZoom() }, viewingReadonlyMode);        // we store the zoom so that when we navigate away and navigate back, that we can set it back to the original zoom
  }, 900);
  const onCenterChanged = debounce((map) => {
    if (!map) return;
    const getCenterFunc = map.getCenter();
    setPostAcqUWMapState({ center: { lat: getCenterFunc.lat(), lng: getCenterFunc.lng() } }, viewingReadonlyMode);
  }, 900);

  function reloadMap() {
    setKey(new Date().toString());
  }

  const PropertyMap = useMemo(() =>
    <MapWrapper
      zoom={zoom}
      center={center}
      mapViewType={mapViewType}
      onZoomChanged={onZoomChanged}
      onCenterChanged={onCenterChanged}
      gestureHandling={gestureHandling}
    >
      <PostAcqUWMap viewingReadonlyMode={viewingReadonlyMode}/>
    </MapWrapper>
    , [zoom, center, mapViewType, gestureHandling, viewingReadonlyMode]);

  const errorBoundaryFallback = (errorInfo) => {
    const error = errorInfo?.error;
    if (error) {
      sendClientLog(
        'Error',
        `Client API - Underwrite Map issue (post-acq task ID = ${selectedOpportunity?.id}); ${error.toString()}`
      );
    }

    return (
      <div className={classes.errorWrapper}>
        <div className={classes.errorHeader}>Error displaying the map</div>
        <Button
          className={classes.reloadMapBtn}
          variant="contained"
          color="primary"
          size="small"
          onClick={reloadMap}
        >
          Reload Map
        </Button>
        <Accordion className={classes.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <span>View details</span>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Typography variant='body2'>
              {error && error.toString()}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>
    )
  }
 
  return (
    <Paper className={classes.myTasksContainer} elevation={3} square id='propertyUWContainer'>
      <PostAcqUWHeader
        containerClass={classes.header}
        viewingReadonlyMode={viewingReadonlyMode}
      />
      <div
        id='propertyUWContentContainer'
        className={classNames(classes.content, classes.currentUserContent)}
      >
        <div className={classes.flexibleSection}>
          <div>
            <div style={{ margin: 8, width: 512 }}>
              <UnderwritePictures module={Constants.Module.POSTACQ} />
              <Divider className={classes.divider} />
              <div className={classes.notesSection}>
                {selectedOpportunity?.notes && <UnderwriteNotes notes={selectedOpportunity.notes} />}
              </div>
            </div>
          </div>
          <div className={classNames(viewingReadonlyMode && classes.grayScale)} style={{ flexGrow: 1, display: 'flex' }}>
            <Paper id='propUWMapSection' className={classes.paperSection} elevation={0} key={key} style={{ flexGrow: 1, minHeight: 487 }}>
              <ErrorBoundary fallbackRender={errorBoundaryFallback}>
                {PropertyMap}
              </ErrorBoundary>
            </Paper>
          </div>
        </div>
        <Divider style={{ margin: '0 8px', zIndex: 1 }} />
        <div id='propUWComparableSection' style={{ marginTop: -8 }}>
          <div className={classNames(viewingReadonlyMode && classes.grayBackground)} style={{ padding: '2px 8px', height: '100%' }}>
            <PostAcqUWGrid viewingReadonlyMode={viewingReadonlyMode}/>
          </div>
        </div>
        {!isUWModal &&
          <>
            <Divider style={{ margin: 6 }} />
            <div id='propUWHistorySection'>
              <PostAcqHistoryTabs transactionEvents={selectedTaskEventHistory} tasks={selectedTaskHistory} taskId={selectedOpportunity?.id}/>
            </div>
          </>
        }
      </div>
    </Paper>
  )
};

const mapStateToProps = ({ postAcqUWState }, ownProps) => {
  const uwState = ownProps.viewingReadonlyMode ? postAcqUWState.underwriteModalState : postAcqUWState;

  return {
    selectedOpportunity: uwState.postAcqUWClientTask,
    zoom: uwState.postAcqUWMapState.zoom,
    center: uwState.postAcqUWMapState.center,
    gestureHandling: uwState.postAcqUWMapState.gestureHandling,
    mapViewType: uwState.postAcqUWMapState.mapViewType,
    selectedTaskHistory: postAcqUWState.selectedTaskHistory,
    selectedTaskEventHistory: postAcqUWState.selectedTaskEventHistory,
  }
};
const mapDispatchToProps = {
  setPostAcqUWMapState
};

const styles = theme => ({
  myTasksContainer: {
    display: 'grid',
    gridTemplateAreas: `
      'header'
      'content'
      `,
    gridTemplateColumns: '1fr',
    gridTemplateRows: '80px 1fr',
    // backgroundColor: 'transparent'
  },
  header: {
    gridArea: 'header',
    width: '100%',
  },
  content: {
    gridArea: 'content',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'scroll',
    padding: '0 8px'
  },
  currentUserContent: {
    boxShadow: '0px 0px 20px 0 transparent',
  },
  flexibleSection: {
    display: 'flex',
    flexWrap: 'wrap',
    minHeight: 'fit-content',
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  paperSection: {
    width: '600px',
    margin: 8,
    // padding: 16,
    borderRadius: 8,
    overflow: 'hidden'
  },
  notesSection: {
    height: '166px',
    overflowY: 'auto'
  },
  errorWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    height: '100%',
  },
  errorHeader: {
    color: '#e63946',
    fontWeight: 700,
    fontSize: '1rem',
  },
  reloadMapBtn: {
    marginTop: '1rem',
    fontSize: '0.8rem',
  },
  accordion: {
    width: '50%',
    boxShadow: 'none',
    position: 'static',
    textAlign: 'center',
    margin: '0 !important',
  },
  accordionSummary: {
    display: 'inline-flex',
    marginLeft: '0.5rem',
  },
  accordionDetails: {
    backgroundColor: '#ffe5ec',
    borderRadius: '0.5rem',
    textAlign: 'start'
  },
  grayScale: {
    filter: 'grayscale(0.3) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff',
    borderRadius: 8
  },
  grayBackground: {
    filter: 'grayscale(0.8) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff'
  }
})
export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PostAcqUWContent);
